import React, {useContext, useCallback, useMemo} from 'react';
import {useNavigate, NavLink} from 'react-router-dom';
import {darken} from '@mui/material/styles';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {DragDropContext, Droppable, Draggable} from '@hello-pangea/dnd';

import {SettingsContext} from '-/contexts/Settings';

import {MENU_GROUPS} from './menuConfig';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Collapse from '@mui/material/Collapse';

export default function DesktopMenuContent({isCollapsed, allItems, preferences}) {
    const navigate = useNavigate();
    const {dispatchStreamLink} = useContext(SettingsContext);
    const {
        shortcuts, expandedGroups, loaded,
        addShortcut, removeShortcut, reorderShortcuts, toggleGroup
    } = preferences;

    const shortcutSet = useMemo(() => new Set(shortcuts), [shortcuts]);

    const shortcutItems = useMemo(() => {
        return shortcuts
            .map(id => allItems.find(item => item.id === id))
            .filter(Boolean);
    }, [shortcuts, allItems]);

    // Group items by their group
    const groupedItems = useMemo(() => {
        const groups = {};
        for (const item of allItems) {
            if (!groups[item.group]) {
                groups[item.group] = [];
            }
            groups[item.group].push(item);
        }
        return groups;
    }, [allItems]);

    // Groups that have items (preserving MENU_GROUPS order)
    const activeGroups = useMemo(() => {
        return MENU_GROUPS.filter(g => groupedItems[g.id]?.length > 0);
    }, [groupedItems]);

    const handleClick = useCallback((e, item) => {
        const {to} = item;
        const {metaKey} = e;
        if (metaKey) {
            window.open(to);
            return;
        }
        navigate(to);
    }, [navigate]);

    const handleDragEnd = useCallback(result => {
        if (!result.destination) {
            return;
        }
        const newOrder = Array.from(shortcuts);
        const [moved] = newOrder.splice(result.source.index, 1);
        newOrder.splice(result.destination.index, 0, moved);
        reorderShortcuts(newOrder);
    }, [shortcuts, reorderShortcuts]);

    const handleDispatchStream = useCallback(() => {
        if (!dispatchStreamLink) {
            return;
        }
        window.open(dispatchStreamLink, '_blank');
    }, [dispatchStreamLink]);

    if (!loaded) {
        return null;
    }

    // --- Collapsed mode ---
    if (isCollapsed) {
        return (
            <Stack sx={{flexGrow: 1, justifyContent: 'space-between', alignItems: 'center'}}>
                <List dense sx={{width: '100%'}}>
                    {shortcutItems.map(item => (
                        <Tooltip key={item.id} title={item.label} placement="right" arrow>
                            <NavLink style={{textDecoration: 'none', color: 'rgba(0, 0, 0, 0.87)'}} to={item.to}>
                                {({isActive}) => (
                                    <ListItem disablePadding>
                                        <ListItemButton
                                            selected={isActive}
                                            onClick={e => handleClick(e, item)}
                                            sx={{justifyContent: 'center', borderRadius: 1}}
                                        >
                                            <ListItemIcon sx={{my: 0.5, minWidth: 'auto'}}>{React.cloneElement(item.icon, {sx: {width: 20, height: 20}})}</ListItemIcon>
                                        </ListItemButton>
                                    </ListItem>
                                )}
                            </NavLink>
                        </Tooltip>
                    ))}

                    <Divider sx={{my: 1}} />

                    {allItems
                        .filter(item => !shortcutSet.has(item.id))
                        .map(item => (
                            <Tooltip key={item.id} title={item.label} placement="right" arrow>
                                <NavLink style={{textDecoration: 'none', color: 'rgba(0, 0, 0, 0.87)'}} to={item.to}>
                                    {({isActive}) => (
                                        <ListItem disablePadding>
                                            <ListItemButton
                                                selected={isActive}
                                                onClick={e => handleClick(e, item)}
                                                sx={{justifyContent: 'center', borderRadius: 1}}
                                            >
                                                <ListItemIcon sx={{my: 0.5, minWidth: 'auto'}}>{React.cloneElement(item.icon, {sx: {width: 20, height: 20}})}</ListItemIcon>
                                            </ListItemButton>
                                        </ListItem>
                                    )}
                                </NavLink>
                            </Tooltip>
                        ))}
                </List>

            </Stack>
        );
    }

    // --- Expanded mode ---
    return (
        <Stack sx={{flexGrow: 1, p: 1, justifyContent: 'space-between'}}>
            <Box>
                {/* Shortcuts section */}
                <Box sx={{mb: 1}}>
                    <DragDropContext onDragEnd={handleDragEnd}>
                        <Droppable droppableId="shortcuts">
                            {provided => (
                                <List
                                    className="menu-nav"
                                    dense
                                    sx={{p: 0}}
                                    ref={provided.innerRef}
                                    {...provided.droppableProps}
                                >
                                    {shortcutItems.map((item, index) => (
                                        <Draggable
                                            key={item.id}
                                            draggableId={item.id}
                                            index={index}
                                        >
                                            {(dragProvided, snapshot) => (
                                                <Box
                                                    ref={dragProvided.innerRef}
                                                    {...dragProvided.draggableProps}
                                                    sx={{
                                                        position: 'relative',
                                                        // Touch devices have no hover: keep the hide action visible at a
                                                        // low opacity instead of revealing it on hover. A hover-triggered
                                                        // visibility change makes iOS Safari swallow the first tap, so
                                                        // links would need two taps to navigate (#826). This drawer is the
                                                        // one an iPad gets in landscape (>= md), so it needs the same
                                                        // guard MenuContent has. The drag handle stays hidden — reordering
                                                        // is pointer-only, and revealing it would re-introduce the
                                                        // swallowed tap.
                                                        '& .shortcut-hide': {opacity: 0.5, transition: 'opacity 0.15s'},
                                                        '& .shortcut-drag': {opacity: 0},
                                                        '@media (hover: hover)': {
                                                            '& .shortcut-hide': {opacity: 0},
                                                            '& .shortcut-drag': {opacity: 0, transition: 'opacity 0.15s'},
                                                            '&:hover .shortcut-hide': {opacity: 0.5},
                                                            '&:hover .shortcut-drag': {opacity: 0.3},
                                                            '& .shortcut-hide:hover': {opacity: 1}
                                                        }
                                                    }}
                                                >
                                                    <NavLink style={{textDecoration: 'none', color: 'rgba(0, 0, 0, 0.87)'}} to={item.to}>
                                                        {({isActive}) => (
                                                            <ListItem disablePadding>
                                                                <ListItemButton
                                                                    selected={!snapshot.isDragging && isActive}
                                                                    onClick={e => handleClick(e, item)}
                                                                    sx={snapshot.isDragging ? {bgcolor: 'action.hover', borderRadius: 1} : {pr: 5}}
                                                                >
                                                                    <ListItemIcon
                                                                        className="shortcut-drag"
                                                                        sx={{
                                                                            cursor: 'grab',
                                                                            position: 'absolute',
                                                                            left: 0,
                                                                            top: '50%',
                                                                            transform: 'translateY(-50%)'
                                                                        }}
                                                                        {...dragProvided.dragHandleProps}
                                                                    >
                                                                        <DragIndicatorIcon fontSize="small" />
                                                                    </ListItemIcon>
                                                                    <ListItemIcon sx={{ml: '6px'}}>{item.icon}</ListItemIcon>
                                                                    <ListItemText primary={item.label} />
                                                                </ListItemButton>
                                                            </ListItem>
                                                        )}
                                                    </NavLink>
                                                    <IconButton
                                                        className="shortcut-hide"
                                                        size="small"
                                                        onClick={() => removeShortcut(item.id)}
                                                        sx={{position: 'absolute', right: 4, top: '50%', transform: 'translateY(-50%)'}}
                                                    >
                                                        <VisibilityOffIcon fontSize="small" />
                                                    </IconButton>
                                                </Box>
                                            )}
                                        </Draggable>
                                    ))}
                                    {provided.placeholder}
                                </List>
                            )}
                        </Droppable>
                    </DragDropContext>
                </Box>

                {/* Grouped sections */}
                <Stack sx={{mt: 1, gap: 1}}>
                    {activeGroups.map(group => {
                        const items = groupedItems[group.id] || [];
                        const isExpanded = expandedGroups.includes(group.id);

                        return (
                            <Box
                                key={group.id}
                                sx={theme => ({
                                    bgcolor: darken(theme.palette.background.default, 0.01),
                                    borderRadius: 1,
                                    overflow: 'hidden'
                                })}
                            >
                                <ListItemButton
                                    onClick={() => toggleGroup(group.id)}
                                    dense
                                >
                                    <ListItemText
                                        primary={group.label}
                                        primaryTypographyProps={{variant: 'overline', sx: {opacity: 0.5}}}
                                    />
                                    <ExpandMoreIcon fontSize="small" color="action" sx={{transition: 'transform 0.2s', transform: isExpanded ? 'rotate(-180deg)' : 'rotate(0deg)'}} />
                                </ListItemButton>

                                <Collapse in={isExpanded}>
                                    <List className="menu-nav" dense sx={{pt: 0}}>
                                        {items.map(item => {
                                            const isShortcut = shortcutSet.has(item.id);

                                            return (
                                                <Box
                                                    key={item.id}
                                                    sx={{
                                                        position: 'relative',
                                                        // Touch devices have no hover: keep the pin visible at a constant
                                                        // opacity. A hover-reveal here makes iOS Safari swallow the first
                                                        // tap on the link (#826).
                                                        '& .pin-action': {opacity: 0.4, transition: 'opacity 0.15s'},
                                                        '@media (hover: hover)': {
                                                            '& .pin-action': {opacity: isShortcut ? 0.4 : 0},
                                                            '&:hover .pin-action': {opacity: isShortcut ? 0.4 : 0.3},
                                                            '& .pin-action:hover': {opacity: 1}
                                                        }
                                                    }}
                                                >
                                                    <NavLink style={{textDecoration: 'none', color: 'rgba(0, 0, 0, 0.87)'}} to={item.to}>
                                                        {({isActive}) => (
                                                            <ListItem disablePadding>
                                                                <ListItemButton
                                                                    selected={isActive}
                                                                    onClick={e => handleClick(e, item)}
                                                                    sx={{pr: 5}}
                                                                >
                                                                    <ListItemIcon>{item.icon}</ListItemIcon>
                                                                    <ListItemText primary={item.label} />
                                                                </ListItemButton>
                                                            </ListItem>
                                                        )}
                                                    </NavLink>
                                                    <IconButton
                                                        className="pin-action"
                                                        size="small"
                                                        onClick={() => isShortcut ? removeShortcut(item.id) : addShortcut(item.id)}
                                                        sx={{position: 'absolute', right: 4, top: '50%', transform: 'translateY(-50%)'}}
                                                    >
                                                        {isShortcut
                                                            ? <PushPinIcon fontSize="small" />
                                                            : <PushPinOutlinedIcon fontSize="small" />}
                                                    </IconButton>
                                                </Box>
                                            );
                                        })}
                                    </List>
                                </Collapse>
                            </Box>
                        );
                    })}
                </Stack>
            </Box>

            {!!dispatchStreamLink && (
                <Button
                    variant="outlined"
                    size="small"
                    endIcon={<OpenInNewIcon />}
                    sx={{mx: 1, mb: 1, mt: 1}}
                    onClick={handleDispatchStream}
                >
                    Open Dispatch Stream
                </Button>
            )}
        </Stack>
    );
}
